import { useState } from 'react';
import clsx from 'clsx';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { STATUS_LABELS, type CreateTaskInput, type Task, type TaskPatch, type TaskStatus } from '../../api/organizer';
import { TaskCard } from './TaskCard';
import { QuickAddTask } from './QuickAddTask';

/** How many completed tasks a column shows before collapsing the rest. */
const DONE_PREVIEW_LIMIT = 10;

interface BoardColumnProps {
  status: TaskStatus;
  tasks: Task[];
  pendingIds: Set<string>;
  onPatch: (taskId: string, patch: TaskPatch) => void;
  onCreate: (input: CreateTaskInput) => void;
  onOpen: (taskId: string) => void;
  onDelete: (taskId: string) => void;
}

export function BoardColumn({
  status,
  tasks,
  pendingIds,
  onPatch,
  onCreate,
  onOpen,
  onDelete,
}: BoardColumnProps) {
  const [showAllDone, setShowAllDone] = useState(false);

  // The column itself is a drop target, not just the sortable list. Without
  // this an empty column cannot be dropped into at all, because there is no
  // sortable item to collide with.
  const { setNodeRef, isOver } = useDroppable({ id: `column:${status}` });

  const isDone = status === 'done';
  const isEmpty = tasks.length === 0;
  const hidden = isDone && !showAllDone ? Math.max(0, tasks.length - DONE_PREVIEW_LIMIT) : 0;
  const visible = hidden > 0 ? tasks.slice(0, DONE_PREVIEW_LIMIT) : tasks;

  return (
    <div className="flex min-w-0 flex-col gap-2 md:flex-1 md:basis-0">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-body-xs font-semibold text-text-tertiary">
          {STATUS_LABELS[status]}
        </h2>
        <span className="text-body-xs text-text-muted">{tasks.length}</span>
      </div>

      <div
        ref={setNodeRef}
        className={clsx(
          'flex min-h-[120px] flex-col gap-2 rounded-xl border p-2 transition-colors',
          isOver ? 'border-text-primary bg-surface-tertiary' : 'border-border-light bg-surface-secondary',
        )}
      >
        <SortableContext
          items={visible.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {visible.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              pending={pendingIds.has(task.id)}
              onPatch={(patch) => onPatch(task.id, patch)}
              onOpen={onOpen}
              onDelete={onDelete}
            />
          ))}
        </SortableContext>

        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setShowAllDone(true)}
            className="rounded-lg px-2 py-1.5 text-body-xs text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary"
          >
            Show {hidden} more
          </button>
        )}

        {isDone && isEmpty && (
          <p className="px-2 py-3 text-center text-body-xs text-text-muted">
            Nothing done yet. Drag a card here when it's finished.
          </p>
        )}

        {!isDone && <QuickAddTask status={status} onCreate={onCreate} compact />}
      </div>
    </div>
  );
}
